import React, { useMemo, useState } from "react";

import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import TableRow from "@mui/material/TableRow";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";

import { format } from "date-fns";
import { truncate } from "lodash";

import { misc, neutral } from "app/themes/colors";
import UploadingStatus from "features/medical-scribe/components/uploading-status.component";
import { getMedicalScribeText } from "features/medical-scribe/medical-scribes-section/scribes-list/utils";
import { ReportTableCell } from "features/reports/components/report-table-cell/report-table-cell.component";
import type { AmbientScribe } from "services/types/ambient-scribe.type";
import { convertHtmlToText } from "utils/common";

import SearchPreview from "./search-preview.component";

interface ScribeListRowProps {
  row: AmbientScribe;
  selected: AmbientScribe | null | undefined;
  dSearch: string;
  onSelect: (row: AmbientScribe | null) => void;
  onEdit?: (row: AmbientScribe) => void;
}

export default function ScribeListRow({
  row,
  selected,
  onSelect,
  onEdit,
  dSearch
}: ScribeListRowProps) {
  const [isExpanded, setIsExpanded] = useState(false);

  // Show expanded if search has value OR user manually expanded
  const shouldShowExpanded = Boolean(dSearch) || isExpanded;

  const previewText = useMemo(() => {
    if (row.preview) {
      return {
        isSearch: true,
        text: ""
      };
    }

    const text =
      [
        convertHtmlToText(row.summary),
        convertHtmlToText(row.soap_notes),
        getMedicalScribeText(row)
      ].find(Boolean) || "";

    return {
      text: truncate(text, {
        length: 512,
        omission: "…"
      }),
      isSearch: false
    };
  }, [row]);

  const showPreview = previewText.text || previewText.isSearch;
  return (
    <React.Fragment>
      <TableRow
        className="table-row-group"
        sx={{
          boxShadow: showPreview ? "none" : undefined,
          background:
            row.id === selected?.id ? misc.selectedSubMenuItem : undefined
        }}
        onClick={() => {
          onSelect(row);
        }}
      >
        <ReportTableCell
          sx={{
            width: "300px",
            fontWeight: 700,
            paddingBottom: showPreview ? 0 : undefined,
            borderBottom: !showPreview
              ? "solid 2px rgba(0,0,0, 0.15)"
              : undefined,
            alignContent: "flex-start"
          }}
        >
          <Stack>
            <Box>
              {row.created_at
                ? format(new Date(row.created_at), "MM/dd/yyyy (h:mmaaa)")
                : ""}
            </Box>
            <Box py={0.5}>
              <UploadingStatus status={row.status} />
            </Box>
          </Stack>
        </ReportTableCell>
        <ReportTableCell
          sx={{
            paddingBottom: showPreview ? 0 : undefined,
            fontWeight: 700,
            borderBottom: !showPreview
              ? "solid 2px rgba(0,0,0, 0.2)"
              : undefined,
            alignContent: "flex-start"
          }}
        >
          {row.duration}
        </ReportTableCell>
        <ReportTableCell
          sx={{
            borderBottom: !showPreview
              ? "solid 2px rgba(0,0,0, 0.2)"
              : undefined,
            alignContent: "flex-start"
          }}
        >
          <Stack direction="row" justifyContent="space-between">
            <Stack direction="row" columnGap={1.5}>
              {row.summary && (
                <Chip
                  size="small"
                  label="Summary"
                  sx={{ background: misc.lightGreen }}
                />
              )}
              {row.soap_notes && (
                <Chip
                  size="small"
                  label="Scribe"
                  sx={{ background: misc.lightGreen }}
                />
              )}
              {row.transcription && (
                <Chip
                  size="small"
                  label="Transcript"
                  sx={{ background: misc.lightGreen }}
                />
              )}
            </Stack>
            {onEdit && (
              <Box pl={1} mr={-1} mt={-0.5}>
                <Button
                  size="small"
                  variant="outlined"
                  color="primary"
                  sx={{ height: 28, minWidth: 0 }}
                  onClick={(e) => {
                    e.stopPropagation();
                    onEdit(row);
                  }}
                >
                  Edit
                </Button>
              </Box>
            )}
          </Stack>
        </ReportTableCell>
      </TableRow>
      {showPreview && (
        <TableRow
          className="table-row-group"
          onClick={() => {
            onSelect(row);
          }}
          sx={{
            boxShadow: showPreview ? "none" : undefined
          }}
        >
          <ReportTableCell
            colSpan={3}
            sx={{
              background:
                row.id === selected?.id ? misc.selectedSubMenuItem : undefined,
              paddingTop: 0,
              paddingBottom: shouldShowExpanded ? 2 : 1,
              borderBottom: "solid 2px rgba(0,0,0, 0.15)"
            }}
          >
            <Stack
              direction="row"
              justifyContent="space-between"
              mt={shouldShowExpanded ? 0 : -0.5}
            >
              <Box width="100%">
                {shouldShowExpanded && (
                  <Box width="100%">
                    {row.preview ? (
                      <SearchPreview preview={row.preview} search={dSearch} />
                    ) : previewText.text ? (
                      <Typography variant="body1" lineHeight={1.6} pb={1}>
                        {previewText.text}
                      </Typography>
                    ) : null}
                  </Box>
                )}
              </Box>

              {!dSearch && (
                <Box pl={2} mr={-1}>
                  <Tooltip
                    title={shouldShowExpanded ? "Collapse" : "Expand"}
                    disableInteractive
                  >
                    <IconButton
                      sx={{
                        border: `solid 1px ${neutral[600]}`,
                        width: 28,
                        height: 28
                      }}
                      onClick={() => {
                        setIsExpanded((prev) => !prev);
                      }}
                    >
                      {shouldShowExpanded ? (
                        <ExpandLessIcon />
                      ) : (
                        <ExpandMoreIcon />
                      )}
                    </IconButton>
                  </Tooltip>
                </Box>
              )}
            </Stack>
          </ReportTableCell>
        </TableRow>
      )}
    </React.Fragment>
  );
}
